import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../ui/Button';
import FormModal, { Field, inputClass } from '../fieldWork/FormModal';
import inductionService from '../../services/induction';
import { errorMessage } from '../../utils/apiResponse';
import { formatDateTimeInTz, useOrgTimezone } from '../../utils/timezone';
import type { InductionRequest } from '../../types/induction';
import { personLabel } from './inductionLabels';

type Decision = { request: InductionRequest; kind: 'approve' | 'decline' };

const STATUS_CLASS: Record<InductionRequest['status'], string> = {
  pending: 'bg-warning-50 text-warning-800',
  approved: 'bg-success-50 text-success-700',
  declined: 'bg-error-50 text-error-700',
  cancelled: 'bg-neutral-100 text-neutral-600',
};

/**
 * Sign-off requests: someone was left off a job, or refused at pre-start,
 * because they are not inducted on a machine. Approving signs them off (as
 * assessed) and, for a job assignment, puts them on the job.
 */
export default function RequestsPanel() {
  const tz = useOrgTimezone();
  const [history, setHistory] = useState(false);
  const [requests, setRequests] = useState<InductionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [decision, setDecision] = useState<Decision | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRequests(await inductionService.requests(history ? 'all' : 'pending'));
    } catch (e) {
      setError(errorMessage(e, 'Could not load requests'));
    } finally {
      setLoading(false);
    }
  }, [history]);
  useEffect(() => { load(); }, [load]);

  const open = (request: InductionRequest, kind: Decision['kind']) => {
    setDecision({ request, kind });
    setNote('');
    setDecisionError(null);
  };

  const submit = async () => {
    if (!decision) return;
    setSaving(true);
    setDecisionError(null);
    try {
      const who = personLabel(decision.request.person_name);
      if (decision.kind === 'approve') {
        const r = await inductionService.approve(decision.request.id, note.trim() || undefined);
        setNotice(r?.assign_error
          ? `${who} is signed off, but could not be put on the job: ${r.assign_error}`
          : r?.assigned ? `${who} is signed off and on the job.` : `${who} is signed off.`);
      } else {
        await inductionService.decline(decision.request.id, note.trim() || undefined);
        setNotice(`Declined the request for ${who}.`);
      }
      setDecision(null);
      load();
    } catch (e) {
      setDecisionError(errorMessage(e, 'Could not save the decision'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="min-w-0 flex-1 basis-56 text-sm text-neutral-600 break-words">
          People left off a job or refused at pre-start because they are not signed off on a machine.
        </p>
        <label className="flex items-center gap-2 text-sm text-neutral-700">
          <input type="checkbox" checked={history} onChange={e => setHistory(e.target.checked)} />
          Show decided
        </label>
      </div>

      {notice && (
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-3 text-sm text-primary-900 break-words">{notice}</div>
      )}
      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 break-words">{error}</div>}
      {loading && requests.length === 0 && <p className="text-sm text-neutral-500">Loading requests…</p>}
      {!loading && requests.length === 0 && !error && (
        <p className="text-sm text-neutral-500">{history ? 'No requests yet.' : 'Nothing waiting for a sign-off.'}</p>
      )}

      <ul className="space-y-3">
        {requests.map(r => (
          <li key={r.id} className="rounded-lg border border-neutral-200 p-3 sm:p-4 text-sm">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <Link to={`/inductions/people/${r.person_ref}`} className="min-w-0 font-semibold text-primary-700 hover:underline break-words">
                {personLabel(r.person_name)}
              </Link>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[r.status]}`}>
                {r.status[0].toUpperCase() + r.status.slice(1)}
              </span>
            </div>
            <dl className="mt-2 space-y-1 text-neutral-700">
              <Row label="Machines">{r.equipment.map(e => e.name).join(', ') || r.inductions.map(i => i.title).join(', ') || '—'}</Row>
              {r.job_id && (
                <Row label="Job">
                  <Link to={`/jobs/${r.job_id}`} className="text-primary-700 hover:underline break-words">{r.job_name || `Job ${r.job_id}`}</Link>
                </Row>
              )}
              <Row label="Why">{r.reason === 'pre_start' ? 'Tried to start the machine' : 'Left off a job assignment'}</Row>
              <Row label="Asked">{[r.requested_by_name, formatDateTimeInTz(r.requested_at, tz)].filter(Boolean).join(', ')}</Row>
              {r.decided_at && (
                <Row label="Decided">
                  {[r.decided_by_name, formatDateTimeInTz(r.decided_at, tz)].filter(Boolean).join(', ')}
                  {r.decision_note && <span className="block text-neutral-500">{r.decision_note}</span>}
                </Row>
              )}
            </dl>
            {r.status === 'pending' && (
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" onClick={() => open(r, 'approve')}>Sign off</Button>
                <Button size="sm" variant="secondary" onClick={() => open(r, 'decline')}>Decline</Button>
              </div>
            )}
          </li>
        ))}
      </ul>

      {decision && (
        <FormModal
          title={decision.kind === 'approve' ? 'Sign off' : 'Decline request'}
          onClose={() => setDecision(null)}
          onSubmit={submit}
          submitLabel={decision.kind === 'approve' ? 'Sign off' : 'Decline'}
          submitting={saving}
          error={decisionError}
        >
          <p className="text-sm text-neutral-700 break-words">
            {decision.kind === 'approve'
              ? <>You are recording that <strong>{personLabel(decision.request.person_name)}</strong> has been inducted on{' '}
                  {decision.request.inductions.map(i => i.title).join(', ') || 'these machines'}.
                  {decision.request.job_id && ' They will be put on the job.'}</>
              : <>The request for <strong>{personLabel(decision.request.person_name)}</strong> will be closed and they stay off the machine.</>}
          </p>
          <Field label="Note (optional)" htmlFor="request-note">
            <textarea id="request-note" className={inputClass} rows={2} value={note} onChange={e => setNote(e.target.value)} />
          </Field>
        </FormModal>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2 min-w-0">
      <dt className="w-16 shrink-0 text-neutral-500">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}
